<template>
  <label
    ref="el"
    :class="classes"
    :tabindex="disabled ? -1 : 0"

    @blur="blur"
    @focus="focus"

    @keydown.self.enter.exact="toggle"
    @keydown.self.space.exact.prevent="toggle"
  >
    <select
      :id="name"
      :name="name"
      :disabled="disabled"
      :placeholder="placeholder"

      :value="value"

      class="cordel-select-element"
      tabindex="-1"

      @input="select"
      @focus="bubble"
    >
      <option />

      <option
        v-for="option in paginated"
        :key="option.key || option.value"
        :value="option.value"
        :disabled="option.disabled"
      >
        {{ option.label }}
      </option>
    </select>

    <button
      class="cordel-select-button"
      tabindex="-1"
      @click="toggle"
    >
      <span class="cordel-select-text">
        {{ filled ? selected : placeholder }}
      </span>

      <font-awesome-icon
        class="cordel-select-chevron"
        icon="chevron-down"
      />
    </button>

    <modal-base
      v-if="selecting"
      ref="modal"

      :title="label"

      marginless
      prefix="Selecionar:"

      @close="toggle(false)"
      @scroll="scroll"
      @loop="last"
    >
      <template v-slot:subheader>
        <form-input
          id=""
          ref="search"

          :value="query"

          class="cordel-select-search"
          name="search"
          placeholder="Pesquisar..."
          icon="search"

          arial-label="Digite para filtrar opções..."

          @input="querying"

          @keydown.tab.exact="first"
          @keydown.down.exact.prevent="start"
        />
      </template>

      <ul
        ref="options"
        class="cordel-select-options"
      >
        <li
          v-for="option in paginated"
          :key="option.key || option.value"

          :disabled="option.disabled"

          class="cordel-select-option"
          tabindex="0"

          @click="e => input(e, option)"
          @keydown.enter.exact="e => input(e, option)"
          @keydown.space.exact.prevent="e => input(e, option)"

          @keydown.up.exact.prevent="previous"
          @keydown.down.exact.prevent="next"

          @keydown.tab.exact="first"
        >
          <span class="cordel-select-option-text">
            {{ option.label }}
          </span>

          <font-awesome-icon
            class="cordel-select-option-chevron"
            icon="chevron-right"
          />
        </li>
      </ul>
    </modal-base>
  </label>
</template>

<script>
  import { empty } from '@sdk/helpers/misc/nullable';
  import search from '@cordel/helpers/form/select/search';

  import FormInput from '@cordel/components/Form/Input.vue';
  import ModalBase from '@cordel/components/Modal/Base.vue';

  const SCROLL_PER_PAGE = 100;

  export default {
    name: 'CordelSelect',
    components: {
      FormInput,
      ModalBase,
    },
    props: {
      name: {
        type: String,
        default: '',
      },
      value: {
        type: [String, Number, Boolean],
        default: '',
      },
      label: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      errors: {
        type: Array,
        default() {
          return [];
        },
      },
      options: {
        type: Array,
        default() {
          return [];
        },
      },
    },
    data() {
      return {
        selecting: false,
        query: null,
        count: SCROLL_PER_PAGE,
        searchTimeout: null,
      };
    },
    computed: {
      classes() {
        const classes = ['cordel-select'];

        if (this.errors.length) {
          classes.push('state--error');
        }

        if (this.disabled) {
          classes.push('state--disabled');
        }

        if (this.loading) {
          classes.push('state--loading');
        }

        if (this.selecting) {
          classes.push('state--open');
        }

        if (this.value) {
          classes.push('state--filled');
        }

        return classes;
      },
      filled() {
        return !empty(this.value);
      },
      selected() {
        const found = this.options.find(o => o.value === this.value);

        return found ? found.label : this.value;
      },
      filtered() {
        if (!this.query) {
          return this.options;
        }

        return this.options
          .filter(option => search(this.query, [option.label, option.value]));
      },
      paginated() {
        return this.filtered.slice(0, this.count);
      },
      max() {
        return this.filtered.length;
      },
    },
    beforeDestroy() {
      clearTimeout(this.searchTimeout);
    },
    methods: {
      input(e, option) {
        if (option.disabled) {
          return;
        }

        this.$emit('input', option.value);
        this.$emit('change', e);

        this.toggle(false);
      },
      blur(e) {
        this.$emit('blur', e);
      },
      focus(e) {
        this.$emit('focus', e);
      },
      bubble() {
        this.navigate(this.$refs.el);
      },
      select(e) {
        this.input(e, e.target);
      },
      toggle(force) {
        if (this.disabled || this.loading) {
          return;
        }

        const forcing = typeof force === 'boolean';

        this.query = null;
        this.selecting = forcing ? force : !this.selecting;

        if (this.selecting) {
          this.blur();
          this.$nextTick(this.search);
        } else if (forcing) {
          this.bubble();
        }
      },
      search() {
        try {
          this.navigate(this.$refs.search.$refs.input);
        } catch (err) { /* */ }
      },
      querying(value) {
        this.query = value;

        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.$emit('search', value);
        }, 500);
      },

      start() {
        this.navigate(this.$refs.options.children[0]);
      },
      previous(e) {
        this.navigate(e.target.previousSibling || this.$refs.search.$refs.input);
      },
      next(e) {
        this.navigate(e.target.nextSibling);
      },
      first(e) {
        let prevent = true;

        if (e.target === this.$refs.search.$refs.input) {
          if (this.filtered.length) {
            prevent = false;
          }
        } else if (e.target.nextSibling) {
          prevent = false;
        }

        if (prevent) {
          e.preventDefault();
          this.navigate(this.$refs.modal.$refs.close);
        }
      },
      last() {
        this.navigate(this.$refs.options.children[0] || this.$refs.search.$refs.input);
      },
      navigate(element) {
        if (element) {
          element.focus();
        }
      },

      scroll(e) {
        if (this.count === this.max) {
          return;
        }

        const {
          options,
        } = this.$refs;

        const {
          offsetHeight,
          scrollTop,
        } = e.target;

        if (scrollTop + offsetHeight + 100 > options.offsetHeight) {
          this.count = Math.min(this.max, this.count + SCROLL_PER_PAGE);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .cordel-select {
    background-color: var(--cordel-legacy-color-neutral-lighter);
    border-radius: var(--cordel-legacy-border-radius-small);
    box-shadow: 0 0 0 var(--cordel-legacy-border-width-soft) var(--cordel-legacy-color-neutral-primary);
    color: var(--cordel-legacy-color-neutral-primary);
    display: block;
    outline: none;
    position: relative;

    &:focus:not(.state--disabled):not(.state--loading) {
      box-shadow: 0 0 0 var(--cordel-legacy-border-width-thin) var(--cordel-legacy-color-highlight-primary);
    }
  }

  .state--filled {
    color: var(--cordel-legacy-color-constant-dark);
  }

  .state--error {
    box-shadow: 0 0 0 var(--cordel-legacy-border-width-thin) var(--cordel-legacy-color-alert-danger);
  }

  .state--disabled {
    background-color: transparent;
    box-shadow: 0 0 0 var(--cordel-legacy-border-width-soft) var(--cordel-legacy-color-neutral-primary);
    color: var(--cordel-legacy-color-neutral-primary);
    cursor: not-allowed;
  }

  .state--loading {
    box-shadow: 0 0 0 var(--cordel-legacy-border-width-soft) var(--cordel-legacy-color-neutral-primary);
    color: var(--cordel-legacy-color-neutral-primary);
  }

  .cordel-select-button {
    @include cordel-input-reset;

    align-items: center;
    color: inherit;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    padding: var(--cordel-legacy-spacing-xxxs) var(--cordel-legacy-spacing-xs);
    position: relative;
    text-align: left;
    width: 100%;

    .state--disabled & {
      cursor: not-allowed;
    }

    .state--loading & {
      cursor: progress;
    }
  }

  .cordel-select-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cordel-select-chevron {
    color: var(--cordel-legacy-color-neutral-primary);
    margin-left: var(--cordel-legacy-spacing-xs);
    transition: transform .1s;

    .state--open & {
      transform: rotate(180deg);
    }
  }

  .cordel-select-element {
    @include cordel-input-reset;

    opacity: 0;
    position: absolute;
    height: 0;
    width: 0;
  }

  .cordel-select-search {
    margin-bottom: var(--cordel-legacy-spacing-xxxs);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin-bottom: var(--cordel-legacy-spacing-xxxs);
    }
  }

  .cordel-select-options {
    list-style: none;
  }

  .cordel-select-option {
    align-items: center;
    border-radius: var(--cordel-legacy-border-radius-small);
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: center;
    padding: var(--cordel-legacy-spacing-xxxs) var(--cordel-legacy-spacing-giga);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: var(--cordel-legacy-spacing-xxxs) var(--cordel-legacy-spacing-s);
    }

    &:focus {
      box-shadow: inset 0 0 0 var(--cordel-legacy-border-width-thin) var(--cordel-legacy-color-highlight-primary);
      outline: none;
    }

    &:hover {
      background-color: var(--cordel-legacy-color-neutral-lighter);
    }

    &[disabled] {
      background-color: none;
      box-shadow: none;
      color: var(--cordel-legacy-color-neutral-primary);
      cursor: not-allowed;
    }
  }

  .cordel-select-option-text {
    flex: 1;
  }

  .cordel-select-option-chevron {
    color: var(--cordel-legacy-color-neutral-primary);
    margin-left: var(--cordel-legacy-spacing-giga);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin-left: var(--cordel-legacy-spacing-s);
    }
  }
</style>
